import { useMemo } from 'react';
import IconButton from '@standardsdigital/ui-components/IconButton';
import Table, { createColumnHelper } from '@standardsdigital/ui-components/Table';
import { useTranslation } from 'react-i18next';
import {
  useDeleteServiceLevelDefinitionMutation,
  useListServiceLevelDefinitionClassifiersQuery,
  useListServiceLevelDefinitionsQuery,
  type ServiceLevelDefinitionDto,
} from 'redux/services/generate/configurations/serviceLevelDefinitions';
import { useLocalizedNavigate, useToast } from 'util/hooks';
import { routes } from 'util/routes';
import styles from './ServiceLevelsDashboardPage.module.css';
import getColumnDefinitions from './getColumnDefinitions';

const columnHelper = createColumnHelper<ServiceLevelDefinitionDto>();

function ServiceLevelsDashboardPage() {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();
  const toast = useToast();

  const { data: serviceLevels, isFetching } = useListServiceLevelDefinitionsQuery(
    {},
  );
  const { data: classifiers, isFetching: isFetchingClassifiers } =
    useListServiceLevelDefinitionClassifiersQuery();
  const [deleteServiceLevelDefinition] = useDeleteServiceLevelDefinitionMutation();

  const openAddServiceLevel = () => {
    void navigate(routes.serviceLevelsCreate.to());
  };

  const openEditServiceLevel = (serviceLevel: ServiceLevelDefinitionDto) => {
    void navigate(
      routes.serviceLevelsDetails.to({
        serviceLevelId: String(serviceLevel.recId),
      }),
    );
  };

  const deleteServiceLevel = (serviceLevel: ServiceLevelDefinitionDto) => {
    void deleteServiceLevelDefinition({ recId: serviceLevel.recId })
      .unwrap()
      .then(() => {
        toast.success(t('serviceLevels.successfullyDeleted'));
      });
  };

  const columns = useMemo(
    () =>
      getColumnDefinitions(
        columnHelper,
        openEditServiceLevel,
        deleteServiceLevel,
        classifiers,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [classifiers],
  );

  const fetching = useMemo(
    () => ({
      isFetching: isFetching || isFetchingClassifiers,
      isFetchingText: t('serviceLevels.fetching'),
      noDataMessage: t('serviceLevels.noServiceLevelsFound'),
    }),
    [isFetching, isFetchingClassifiers, t],
  );

  let tableData: ServiceLevelDefinitionDto[] = [];
  if (classifiers !== undefined) {
    tableData = serviceLevels ?? [];
  }

  return (
    <section className={styles.pageWrapper}>
      <div className={styles.page}>
        <div className={styles.pageHeader}>
          <h1 className="sd-heading-lg">{t('navigation.serviceLevels')}</h1>
          <IconButton icon="Plus" onClick={openAddServiceLevel}>
            {t('serviceLevels.addServiceLevel')}
          </IconButton>
        </div>
        <div className={styles.tableView}>
          <Table
            className={styles.table}
            aria-label={t('serviceLevels.tableAriaLabel')}
            columns={columns}
            tableWidth="100%"
            data={tableData}
            fetching={fetching}
            data-auto="service-level-table"
          />
        </div>
      </div>
    </section>
  );
}

export default ServiceLevelsDashboardPage;
